
import { defineComponent, nextTick } from "vue";
import store from "@/store";

import StudentsService from "@/api/Aula/StudentsService";
import IndividualEnrollmentService from "@/api/Enrollment/IndividualEnrollmentService";

import AcademicAssignmentsService from "@/api/Academic/SystemAcademicAssignmentsService";
import AcademicTeacherAssigmentService from "@/api/Academic/AcademicTeacherAssigmentService";
import PeriodsService from "@/api/Configuration/PeriodsService";

import SaberTypesService from "@/api/AcademicBase/SaberTypesService";
import AcademicYearSaberWeightsService from "@/api/AcademicBase/SaberWeightsService";
import AcademicCompetenciesService from "@/api/AcademicBase/AcademicCompetenciesService";
import AcademicPerformanceGradesService from "@/api/AcademicBase/AcademicPerformanceGradesService";
import CompetencyPeriodAssignmentsService from "@/api/AcademicBase/CompetencyPeriodAssignmentsService";
import AcademicPerformancesService from "@/api/AcademicBase/AcademicPerformancesService";
import SystemEvaluationMetricsService from "@/api/Academic/SystemEvaluationMetricsService";

import type {
  IStudentsModel,
  ISaberTypesModel,
  ISaberWeightsModel,
  IAcademicCompetenciesModel,
  IAcademicPerformancesModel,
  IAcademicPerformanceGradesModel,
  ICompetencyPeriodAssignmentsModel,
  ISystemEvaluationMetricsModel,
  IIndividualEnrollmentModel,
  IPeriodsModel,
  ISystemSubjectsModel,
  ICourseGroupsModel
} from "@/interfaces/modules";
import SystemSubjectsService from "@/api/Academic/SystemSubjectsService";
import CourseGroupsService from "@/api/Aula/CourseGroupsService";
import AcademicYear from "@/views/Configuration/AcademicYear.vue";

type SaberCode = string;

type FieldDef = {
  key: string;
  label?: string;
  thClass?: string | string[];
  tdClass?: string | string[];
  thStyle?: Record<string, string>;
  thAttr?: Record<string, string>;
  stickyColumn?: boolean;
};

type SaberUi = ISaberTypesModel & {
  percent: number;
  competencies: Array<IAcademicCompetenciesModel & { percent: number; DisplayCode: string }>;
  color?: string;
};

export default defineComponent({
  name: "AcademicEvaluation",

  data() {
    return {
      /* resolved from the assignment chain */
      assignmentId: this.$route.params.Id || 0,
      academicYearId: 0,
      subjectId: 0,
      courseGroupId: 0,
      teacherId: 0,

      /* periods */
      periodId: 0, // current period (for edit gate UI)
      periods: [] as IPeriodsModel[],
      selectedPeriodId: "" as string, // "" = ALL
      selectedPeriodIds: [] as number[], // multi-select support for period chips


      loading: false,
      saving: false,

      canEdit: false,
      periodGate: {
        id: 0,
        number: 0,
        description: "",
        openFrom: null as string | null,
        openTo: null as string | null,
      },

      /* students */
      students: [] as IStudentsModel[],
      enrollments: [] as IIndividualEnrollmentModel[],
      subjects: [] as ISystemSubjectsModel[],
      groups: [] as ICourseGroupsModel[],

      photo: { open: false, url: null as string | null, initials: "", name: "" },

      /* sabers/competencies per period */
      baseSaberList: [] as SaberUi[],
      periodSaberMap: {} as Record<number, SaberUi[]>,
      visibleSabers: {} as Record<SaberCode, boolean>,
      visiblePeriods: {} as Record<number, boolean>,

      /* grades keyed by student and composed keys */
      scores: {} as Record<number, Record<string, number | string>>,
      scoreIds: {} as Record<number, Record<string, number>>,
      /* legacy direct-score shown on Subtotal for competency-enabled sabers */
      legacySubtotals: {} as Record<number, Record<string, number>>,

      draft: { savedAt: null as Date | null, savedAtShort: "", savedAtTitle: "" },

      autosave: { enabled: true, minutes: 1 as number },
      autosaveTimer: null as any,

      navigatorOnline: typeof navigator !== "undefined" ? navigator.onLine : true,

      float: { visible: false, style: {} as Record<string, string> },

      metrics: [] as ISystemEvaluationMetricsModel[],
      minScore: null as number | null,
      maxScore: null as number | null,

      errors: {} as Record<string, string>,

      competencySaberCode: "",
    };
  },

  computed: {
    periodOptions(): Array<{ value: string; text: string }> {
      const base = [{ value: "", text: "Todos" }];

      const opts = (this.periods || []).map((p: any) => {
        const pid = Number(p.Id);
        const baseLabel = `P${p.PeriodNumber ?? pid} — ${p.Description ?? "Sin descripción"}`;
        const isCurrent = this.periodId && pid === Number(this.periodId);
        return {
          value: String(pid),
          text: isCurrent ? `${baseLabel} (Actual)` : baseLabel,
        };
      });

      return base.concat(opts);
    },

    currentPeriodObj(): any | null {
      // Prefer the period that matches periodId (resolved as "current")
      const pid = Number(this.periodId || 0);
      if (pid && this.periods && this.periods.length) {
        const direct = this.periods.find((p: any) => Number(p.Id) === pid);
        if (direct) return direct;
      }

      // Fallback: first visible period in toggles
      if (this.visiblePeriods) {
        const firstVisibleId = Object.keys(this.visiblePeriods)
          .map((k) => Number(k))
          .find((id) => this.visiblePeriods[id]);
        if (firstVisibleId) {
          return (this.periods || []).find(
            (p: any) => Number(p.Id) === firstVisibleId
          ) || null;
        }
      }

      return null;
    },

    currentPeriodLabel(): string {
      const p = this.currentPeriodObj;
      if (!p) return "Todos los períodos";

      const num = (p as any).PeriodNumber ?? p.Id;
      const desc = (p as any).Description ?? "";
      const base = `P${num}`;
      return desc ? `${base} — ${desc}` : base;
    },


    periodsToDraw(): Array<any> {
      // Devolvemos todos; lo que realmente se dibuja lo controla visiblePeriods
      return this.periods || [];
    },


    currentSubjectName(): string {
      const subjectId = Number(this.subjectId);
      if (!subjectId) return '';

      console.log('subjectId', this.subjectId);

      // Ajusta la ruta según tu módulo de Vuex
      const s = (this.subjects || []).find(x => x.Id === subjectId);
      return s ? s.Name : `Asignatura ${subjectId}`;
    },
    currentGroupName(): string {
      const _course = Number(this.courseGroupId);
      if (!_course) return '';

      const g = (this.groups || []).find(x => x.Id === this.courseGroupId);
      return g ? g.Name : '';
    },

    tableFields(): FieldDef[] {
      const base: FieldDef[] = [
        { key: "StudentName", label: "Estudiante", stickyColumn: true, thClass: "bg-white z-3", tdClass: "" },
      ];
      const dynamic: FieldDef[] = [];
      let hasCompetencies = false;

      for (const p of this.periodsToDraw) {
        const periodId = Number(p.Id);
        if (!this.visiblePeriods[periodId]) continue;
        const sabers = this.periodSaberMap[periodId] || [];

        for (const s of sabers) {
          if (!this.visibleSabers[s.Code]) continue;

          if (s.competencies.length) {
            for (const c of s.competencies) {

              if (c.SubjectId !== this.subjectId) continue;

              hasCompetencies = true;

              const key = this.keyPerf(periodId, s.Code, c.Id);
              dynamic.push({
                key,
                label: `${c.DisplayCode} (${Number(c.percent) || 0}%)`,
                tdClass: "p-0",
                thClass: "p-0 text-center fw-semibold",
                thStyle: { background: this.compColor(c) || s.color || "" },
                thAttr: { title: (c as any).Description || (c as any).Title },
              });
            }
            const subKey = this.keySubtotal(periodId, s.Code);
            dynamic.push({
              key: subKey,
              label: `Final (100%)`,
              tdClass: "p-0",
              thClass: "p-0 text-center fw-semibold",
              thStyle: { background: s.color || "" },
              thAttr: { title: `${s.Name}: promedio ponderado de desempeños (las ponderaciones de desempeños suman 100%)` },
            });
          } else {
            const key = this.keyDirectSaber(periodId, s.Code);
            dynamic.push({
              key,
              label: `${s.Name} (${s.percent}%)`,
              tdClass: "p-0",
              thClass: "p-0 text-center fw-semibold",
              thStyle: { background: s.color || "" },
              thAttr: { title: `${s.Name}: evaluación directa` },
            });
          }
        }
      }


      const hasPeriodMap =
        this.periodSaberMap &&
        typeof this.periodSaberMap === "object" &&
        Object.keys(this.periodSaberMap).length > 0;

      if (!hasCompetencies && hasPeriodMap) {
        this.notify("No se encontro ninguna competencia para la asignatura", "danger");
        return base;
        //  this.$router.push("/aula/my-groups");

      }

      dynamic.push({
        key: "FinalScore",
        label: "Final (100%)",
        tdClass: "p-0",
        thClass: "p-0 text-center fw-bold text-white",
        thStyle: { background: "#198754" },
      });

      return base.concat(dynamic);
    },

    periodHeaderGroups(): Array<{ periodId: number; label: string; colspan: number; headerStyle: any }> {
      const groups: Array<{ periodId: number; label: string; colspan: number; headerStyle: any }> = [];

      for (const p of this.periodsToDraw) {
        const periodId = Number(p.Id);
        if (!this.visiblePeriods[periodId]) continue;

        const sabers = this.periodSaberMap[periodId] || [];
        let colCount = 0;

        for (const s of sabers) {
          if (!this.visibleSabers[s.Code]) continue;
          colCount += s.competencies.length ? s.competencies.length + 1 : 1;
        }

        if (colCount > 0) {
          groups.push({
            periodId,
            label: `${p.PeriodNumber ?? p.Id}`,
            colspan: colCount,
            headerStyle: { background: "#eef5ff" },
          });
        }
      }
      return groups;
    },

    saberHeaderGroups(): Array<{ periodId: number; code: string; name: string; percent: number; colspan: number; headerStyle: any }> {
      const groups: Array<{ periodId: number; code: string; name: string; percent: number; colspan: number; headerStyle: any }> = [];

      for (const p of this.periodsToDraw) {
        const periodId = Number(p.Id);
        if (!this.visiblePeriods[periodId]) continue;
        const sabers = this.periodSaberMap[periodId] || [];

        for (const s of sabers) {
          if (!this.visibleSabers[s.Code]) continue;

          const cols = s.competencies.length ? s.competencies.length + 1 : 1;
          groups.push({
            periodId,
            code: s.Code,
            name: s.Name,
            percent: s.percent,
            colspan: cols,
            headerStyle: { background: s.color || "" },
          });
        }
      }
      return groups;
    },

    dynamicValueFields(): Array<{ key: string }> {
      const arr: Array<{ key: string }> = [];

      for (const p of this.periodsToDraw) {
        const periodId = Number(p.Id);
        if (!this.visiblePeriods[periodId]) continue;

        const sabers = this.periodSaberMap[periodId] || [];
        for (const s of sabers) {
          if (!this.visibleSabers[s.Code]) continue;

          if (s.competencies.length) {
            for (const c of s.competencies) arr.push({ key: this.keyPerf(periodId, s.Code, c.Id) });
          } else {
            arr.push({ key: this.keyDirectSaber(periodId, s.Code) });
          }
        }
      }
      return arr;
    },

    subtotalFields(): Array<{ key: string }> {
      const arr: Array<{ key: string }> = [];
      for (const p of this.periodsToDraw) {
        const periodId = Number(p.Id);
        if (!this.visiblePeriods[periodId]) continue;

        const sabers = this.periodSaberMap[periodId] || [];
        for (const s of sabers) {
          if (this.visibleSabers[s.Code] && s.competencies.length) {
            arr.push({ key: this.keySubtotal(periodId, s.Code) });
          }
        }
      }
      return arr;
    },

    tableItems(): Array<any> {
      return this.students.map((st) => {
        const name = `${st.Name || ""} ${st.LastName || ""}`.trim();
        const initials = (name.split(" ").map((p) => p[0]).join("").substring(0, 2) || "ST").toUpperCase();

        const row: any = {
          Id: st.Id,
          StudentName: name,
          Initials: initials,
          PhotoUrl: (st as any).Image || (st as any).PhotoUrl || null,
          Document: (st as any).IdentificationNumber || "",
        };

        if (!this.scores[st.Id]) this.scores[st.Id] = {};
        if (!this.legacySubtotals[st.Id]) this.legacySubtotals[st.Id] = {};

        let final = 0;

        for (const p of this.periodsToDraw) {
          const periodId = Number(p.Id);
          if (!this.visiblePeriods[periodId]) continue;

          const sabers = this.periodSaberMap[periodId] || [];
          for (const s of sabers) {
            if (!this.visibleSabers[s.Code]) continue;

            if (s.competencies.length) {
              let weightedSum = 0;
              let wSum = 0;

              for (const c of s.competencies) {
                const k = this.keyPerf(periodId, s.Code, c.Id);
                const raw = this.scores[st.Id][k];
                const v = typeof raw === "string" ? Number(raw) : (raw as number);
                const w = Number(c.percent) || 0;
                if (typeof v === "number" && !isNaN(v) && w > 0) {
                  const minB = this.minScore ?? 0;
                  const maxB = this.maxScore ?? 5;
                  const clamped = Math.max(minB, Math.min(maxB, v));
                  weightedSum += clamped * w;
                  wSum += w;
                }
                row[k] = typeof raw === "number" ? raw : (raw ?? undefined);
              }

              const subKey = this.keySubtotal(periodId, s.Code);
              const avg = wSum > 0 ? (weightedSum / wSum) : undefined;

              let subValue: number | undefined = avg == null ? undefined : Math.round((avg + Number.EPSILON) * 100) / 100;
              if (subValue == null) {
                const legacy = this.legacySubtotals[st.Id]?.[subKey];
                if (typeof legacy === "number" && !isNaN(legacy)) {
                  subValue = Math.round((legacy + Number.EPSILON) * 100) / 100;
                }
              }
              row[subKey] = subValue;

              if (row[subKey] != null) final += row[subKey] * (s.percent / 100);
            } else {
              const key = this.keyDirectSaber(periodId, s.Code);
              const raw = this.scores[st.Id][key];
              const v = typeof raw === "string" ? Number(raw) : (raw as number);
              const minB = this.minScore ?? 0;
              const maxB = this.maxScore ?? 5;
              const clamped = typeof v === "number" && !isNaN(v) ? Math.max(minB, Math.min(maxB, v)) : undefined;
              row[key] = clamped;
              if (row[key] != null) final += row[key] * (s.percent / 100);
            }
          }
        }

        row.FinalScore = Math.round((final + Number.EPSILON) * 100) / 100;
        return row;
      });
    },

    hasAnythingToSave(): boolean {
      return Object.values(this.scores).some((row) => Object.keys(row || {}).length > 0);
    },
    hasValidationErrors(): boolean {
      return Object.keys(this.errors).length > 0;
    },
  },

  async mounted() {
    window.addEventListener("online", this.onOnline);
    window.addEventListener("offline", this.onOffline);

    this.loadAutosaveSettings();
    await this.load();
    await this.loadEvaluationLimits();
    this.loadLocalDraft();

    await nextTick();
    this.buildFloatingHeader();
    this.syncFloatingHeader();

    window.addEventListener("scroll", this.onScroll, { passive: true });
    window.addEventListener("resize", this.syncFloatingHeader, { passive: true });

    const host = this.$refs.tableScroll as HTMLElement | undefined;
    if (host) host.addEventListener("scroll", this.onHScroll, { passive: true });

    window.addEventListener("beforeunload", this.beforeUnloadHandler);

    this.restartAutosaveTimer();
  },

  beforeUnmount() {
    window.removeEventListener("scroll", this.onScroll as any);
    window.removeEventListener("resize", this.syncFloatingHeader as any);
    window.removeEventListener("online", this.onOnline as any);
    window.removeEventListener("offline", this.onOffline as any);
    window.removeEventListener("beforeunload", this.beforeUnloadHandler as any);

    const host = this.$refs.tableScroll as HTMLElement | undefined;
    if (host) host.removeEventListener("scroll", this.onHScroll as any);

    this.stopAutosaveTimer();
  },

  watch: {
    selectedPeriodId(newVal: string) {
      // Sync array selection cuando el usuario usa el dropdown
      if (!newVal) {
        // Vacío = todos los períodos
        this.selectedPeriodIds = (this.periods || []).map((p: any) => Number(p.Id));
      } else {
        const id = Number(newVal);
        if (!isNaN(id)) {
          this.selectedPeriodIds = [id];
        }
      }
    },

    selectedPeriodIds(newList: number[]) {
      // Mantener el dropdown más o menos alineado con los chips
      if (!newList || !newList.length) {
        this.selectedPeriodId = "";
        return;
      }

      const allIds = (this.periods || []).map((p: any) => Number(p.Id));
      const allSelected =
        newList.length === allIds.length &&
        newList.every((id) => allIds.includes(Number(id)));

      if (allSelected) {
        this.selectedPeriodId = "";
      } else if (newList.length === 1) {
        this.selectedPeriodId = String(newList[0]);
      } else {
        // Varios períodos seleccionados: dejamos el primero como representativo en el dropdown
        this.selectedPeriodId = String(newList[0]);
      }
    },
  },


  methods: {
    /* ========== KEY HELPERS (period-aware) ========== */
    keyPerf(periodId: number, code: string, compOrPerfId: number) { return `P${periodId}.${code}.C${compOrPerfId}`; },
    keySubtotal(periodId: number, code: string) { return `P${periodId}.${code}.Subtotal`; },
    keyDirectSaber(periodId: number, code: string) { return `P${periodId}.${code}`; },

    /* ========== ERRORS ========== */
    cellKey(id: number, key: string) { return `${id}|${key}`; },
    cellError(id: number, key: string): string { return this.errors[this.cellKey(id, key)] || ""; },

    /* ========== VISIBILITY ========== */
    toggleSaber(code: string) { this.visibleSabers[code] = !this.visibleSabers[code]; },
    async toggleSaberAndSync(code: string) {
      this.toggleSaber(code);
      await nextTick();
      this.buildFloatingHeader();
      this.syncFloatingHeader();
    },
    togglePeriod(periodId: number) { this.visiblePeriods[periodId] = !this.visiblePeriods[periodId]; },
    async togglePeriodAndSync(periodId: number) {
      this.togglePeriod(periodId);
      await nextTick();
      this.buildFloatingHeader();
      this.syncFloatingHeader();
    },
    onSelectedPeriodChanged() {
      const selected = this.selectedPeriodId;
      const all = this.periods || [];

      const map: Record<number, boolean> = {};

      if (!selected) {
        // "" = Todos los períodos visibles
        for (const p of all) {
          map[Number(p.Id)] = true;
        }
      } else {
        const selId = Number(selected);
        for (const p of all) {
          const pid = Number(p.Id);
          // Solo el seleccionado visible por defecto
          map[pid] = pid === selId;
        }
      }

      this.visiblePeriods = map;

      this.$nextTick(() => {
        this.buildFloatingHeader();
        this.syncFloatingHeader();
      });
    },


    isDynamicValueField(key: string) { return this.dynamicValueFields.some((f) => f.key === key); },
    isSubtotalField(key: string) { return this.subtotalFields.some((f) => f.key === key); },

    /* ========== COLORS ========== */
    colorFromStr(seed: string) {
      const SABER_COLORS = ["#FFC107", "#42A5F5", "#66BB6A"];
      let h = 0; for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
      return SABER_COLORS[h % SABER_COLORS.length];
    },
    compColor(c: { Id: number }) {
      const PERF_COLORS = [
        "#FDE2E4", "#FAD2E1", "#FFD6EA", "#FDE7F3", "#FFDCF5", "#F6D8FF", "#EBD5F5",
        "#F3E8FF", "#E9D7FE", "#EFD3F5", "#E6D0FF", "#F9DDE7", "#F7D6FF", "#F8D7EB",
        "#F2CCFF", "#EBD9FF", "#EED7FA", "#F5D9F6", "#FAD9E6", "#FCE0F1", "#F1D4F4",
        "#ECD9F2", "#FFE0F2", "#FDE1F0", "#F5D6ED", "#F2D7FF", "#EEDCFF", "#EAD6FF",
      ];
      const id = Number(c?.Id || 0);
      return PERF_COLORS[id >= 0 ? id % PERF_COLORS.length : 0];
    },

    /* ========== STYLES/TOOLTIPS (period-aware) ========== */
    getColStyle(fieldKey: string) {
      const m = /^P(\d+)\.([^.]+)(?:\.(.+))?$/.exec(fieldKey);
      if (!m) return {};
      const periodId = Number(m[1]);
      const code = m[2];
      const tail = m[3];

      if (!this.visiblePeriods[periodId]) return {};
      const sabers = this.periodSaberMap[periodId] || [];
      const s = sabers.find(x => x.Code === code);
      if (!s || !this.visibleSabers[code]) return {};

      if (!tail || tail === "Subtotal") {
        return { background: s.color || "", padding: "0.08rem 0.20rem" };
      }
      if (tail.startsWith("C")) {
        const cid = Number(tail.substring(1));
        const c = s.competencies.find(cc => Number(cc.Id) === cid);
        if (c) return { background: this.compColor(c) || s.color || "", padding: "0.08rem 0.20rem" };
      }
      return {};
    },
    getTooltip(fieldKey: string) {
      const m = /^P(\d+)\.([^.]+)(?:\.(.+))?$/.exec(fieldKey);
      if (!m) return "";
      const periodId = Number(m[1]);
      const code = m[2];
      const tail = m[3];

      if (!this.visiblePeriods[periodId]) return "";
      const sabers = this.periodSaberMap[periodId] || [];
      const s = sabers.find(x => x.Code === code);
      if (!s || !this.visibleSabers[code]) return "";

      if (tail === "Subtotal") return `${s.Name}: promedio ponderado de desempeños`;
      if (!tail) return `${s.Name}: evaluación directa`;
      if (tail.startsWith("C")) {
        const cid = Number(tail.substring(1));
        const c = s.competencies.find(cc => Number(cc.Id) === cid);
        if (c) return (c as any).Description || (c as any).Title;
      }
      return "";
    },

    /* ========== AUTOSAVE SETTINGS ========== */
    autosaveKey(): string {
      const subj = this.subjectId || "any";
      const grp = this.courseGroupId || "any";
      return `grades-autosave:ay=${this.academicYearId}:subj=${subj}:grp=${grp}`;
    },
    loadAutosaveSettings() {
      try {
        const raw = localStorage.getItem(this.autosaveKey());
        if (!raw) {
          this.autosave.enabled = true;
          this.autosave.minutes = 1;
          this.saveAutosaveSettings();
          return;
        }
        const cfg = JSON.parse(raw) as { enabled?: boolean; minutes?: number };
        this.autosave.enabled = cfg?.enabled !== false;
        this.autosave.minutes = 1;
      } catch {
        this.autosave.enabled = true;
        this.autosave.minutes = 1;
      }
    },
    saveAutosaveSettings() {
      try { localStorage.setItem(this.autosaveKey(), JSON.stringify({ enabled: this.autosave.enabled, minutes: 1 })); }
      catch (err: any) { this.notify("error " + err.message, "danger"); }
    },
    onAutosaveToggle() {
      this.saveAutosaveSettings();
      this.restartAutosaveTimer();
      this.notify("Auto-guardado " + (this.autosave.enabled ? "activado." : "desactivado."), "info");
    },
    restartAutosaveTimer() {
      this.stopAutosaveTimer();
      if (!this.autosave.enabled) return;
      const ms = 60 * 1000;
      this.autosaveTimer = setInterval(() => {
        if (this.hasAnythingToSave && !this.hasValidationErrors) this.saveLocalDraft(true);
      }, ms);
    },
    stopAutosaveTimer() { if (this.autosaveTimer) { clearInterval(this.autosaveTimer); this.autosaveTimer = null; } },

    /* ========== DRAFT HELPERS & DRAFT ========== */
    validFieldKeysSet(): Set<string> {
      const set = new Set<string>();
      for (const p of this.periodsToDraw) {
        const periodId = Number(p.Id);
        const sabers = this.periodSaberMap[periodId] || [];
        for (const s of sabers) {
          if (!s.competencies?.length) { set.add(this.keyDirectSaber(periodId, s.Code)); continue; }
          for (const c of s.competencies) set.add(this.keyPerf(periodId, s.Code, c.Id));
        }
      }
      return set;
    },
    /** NEW: compute all valid editable keys across ALL periods, not only the selected one. */
    allValidValueKeys(): Set<string> {
      const set = new Set<string>();
      for (const p of this.periods) {
        const periodId = Number(p.Id);
        const sabers = this.periodSaberMap[periodId] || [];
        for (const s of sabers) {
          if (!s.competencies?.length) { set.add(this.keyDirectSaber(periodId, s.Code)); continue; }
          for (const c of s.competencies) set.add(this.keyPerf(periodId, s.Code, c.Id));
        }
      }
      return set;
    },

    filteredCopyOfScoresForDraft(): Record<number, Record<string, number | string>> {
      const valid = this.validFieldKeysSet();
      const studentIds = new Set(this.students.map((s) => s.Id));
      const out: Record<number, Record<string, number | string>> = {};
      for (const sidStr of Object.keys(this.scores)) {
        const sid = Number(sidStr);
        if (!studentIds.has(sid)) continue;
        const row = this.scores[sid] || {};
        const dst: Record<string, number | string> = {};
        for (const [k, v] of Object.entries(row)) {
          if (!valid.has(k)) continue;
          if (typeof v === "number" || typeof v === "string") dst[k] = v;
        }
        if (Object.keys(dst).length) out[sid] = dst;
      }
      return out;
    },
    draftKey(): string {
      const subj = this.subjectId || "any";
      const grp = this.courseGroupId || "any";
      return `grades-draft:ay=${this.academicYearId}:subj=${subj}:grp=${grp}`;
    },
    onManualSaveDraft() {
      if (this.hasValidationErrors) { this.notify("Corrige las celdas inválidas antes de guardar el borrador.", "warning"); return; }
      this.saveLocalDraft(false);
    },
    saveLocalDraft(silent = true) {
      try {
        const nowIso = new Date().toISOString();
        const payload = { when: nowIso, schema: { fieldKeys: Array.from(this.validFieldKeysSet()) }, scores: this.filteredCopyOfScoresForDraft() };
        localStorage.setItem(this.draftKey(), JSON.stringify(payload));
        this.setDraftInfo(new Date(nowIso));
        if (!silent) this.notify("Borrador guardado localmente.", "success");
      } catch { if (!silent) this.notify("No se pudo guardar el borrador local.", "danger"); }
    },
    loadLocalDraft() {
      try {
        const raw = localStorage.getItem(this.draftKey());
        if (!raw) return;
        const parsed = JSON.parse(raw) as { when?: string };
        if (parsed?.when) this.setDraftInfo(new Date(parsed.when));
      } catch (err: any) { this.notify("error " + err.message, "danger"); }
    },
    restoreLocalDraftUI() {
      try {
        const raw = localStorage.getItem(this.draftKey());
        if (!raw) { this.notify("No se encontró ningún borrador para restaurar.", "warning"); return; }
        const parsed = JSON.parse(raw) as { when?: string; scores?: Record<string, Record<string, unknown>>; schema?: { fieldKeys?: string[] } };
        const draftScores: Record<string, Record<string, unknown>> = parsed?.scores ?? {};
        const draftFieldKeys: string[] = Array.isArray(parsed?.schema?.fieldKeys) ? (parsed!.schema!.fieldKeys as string[]) : [];
        const currentValid = this.validFieldKeysSet();
        const allowedKeys = new Set<string>(draftFieldKeys.length ? draftFieldKeys.filter((k) => currentValid.has(k)) : Array.from(currentValid));
        const restored: Record<number, Record<string, number | string>> = {};
        const currentIds = new Set(this.students.map((s) => s.Id));

        for (const sidStr of Object.keys(draftScores)) {
          const sid = Number(sidStr);
          if (!currentIds.has(sid)) continue;

          const row = draftScores[sidStr] || {};
          const out: Record<string, number | string> = {};
          for (const [k, v] of Object.entries(row)) {
            if (!allowedKeys.has(k)) continue;
            if (typeof v === "number" && Number.isFinite(v)) {
              const minB = this.minScore ?? 0, maxB = this.maxScore ?? 5;
              const n = Math.max(minB, Math.min(maxB, v));
              out[k] = Math.round((n + Number.EPSILON) * 100) / 100;
            } else if (typeof v === "string") out[k] = v;
            else if (v != null && typeof (v as any).toString === "function") out[k] = String(v);
            else out[k] = "";
          }
          if (Object.keys(out).length) restored[sid] = out;
        }

        this.scores = restored;

        this.$nextTick().then(() => {
          for (const st of this.students) this.recalcForStudent(st.Id);
          this.syncFloatingHeader();
        });

        this.setDraftInfo(parsed.when ? new Date(parsed.when) : new Date());
        this.notify("Borrador restaurado en la tabla.", "success");
      } catch (e) {
        console.error("restoreLocalDraftUI error", e);
        this.notify("No se pudo restaurar el borrador.", "danger");
      }
    },
    clearLocalDraft() {
      try { localStorage.removeItem(this.draftKey()); this.setDraftInfo(null); this.notify("Borrador local eliminado.", "secondary"); }
      catch (err: any) { this.notify("error " + err.message, "danger"); }
    },
    setDraftInfo(d: Date | null) {
      this.draft.savedAt = d;
      if (!d) { this.draft.savedAtShort = ""; this.draft.savedAtTitle = ""; return; }

      const fmt = new Intl.DateTimeFormat("es-CO", {
        timeZone: "America/Bogota", year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", hour12: false,
      });
      const local = fmt.format(d);
      const normalized = local.replace(", ", " ");
      this.draft.savedAtShort = normalized;
      this.draft.savedAtTitle = `Guardado: ${normalized} (hora de Colombia)`;
    },
    beforeUnloadHandler() { if (this.hasAnythingToSave && !this.hasValidationErrors) this.saveLocalDraft(true); },

    /* ========== HARD ENFORCEMENT (inputs) ========== */
    numericKeyGuard(evt: KeyboardEvent) {
      const allowed = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Home", "End", "Enter"];
      const isCtrl = evt.ctrlKey || evt.metaKey;
      const key = evt.key;
      if (isCtrl && ["a", "c", "v", "x", "z", "y"].includes(key.toLowerCase())) return;
      if (allowed.includes(key)) return;
      if (/^[0-9.]$/.test(key)) return;
      evt.preventDefault();
    },
    onCellInputHard(id: number, key: string, e: Event) {
      const input = e.target as HTMLInputElement | null;
      const vRaw = input ? input.value : "";
      let v = (vRaw || "").trim();
      if (v === "") { delete this.errors[this.cellKey(id, key)]; this.scores[id][key] = ""; return; }
      if ((v.match(/\./g) || []).length > 1 || v === "." || v === "-." || v === "-") { this.hardReject(id, key, input); return; }

      const num = Number(v);
      const minB = this.minScore ?? 0, maxB = this.maxScore ?? 5;
      if (!Number.isFinite(num) || num < minB || num > maxB) { this.hardReject(id, key, input, minB, maxB); return; }

      delete this.errors[this.cellKey(id, key)];
      this.scores[id][key] = v;
    },
    onCellCommit(id: number, key: string) {
      const raw = this.scores[id]?.[key];
      const minB = this.minScore ?? 0, maxB = this.maxScore ?? 5;
      if (raw === "" || raw == null) return;
      const n = typeof raw === "string" ? Number(raw) : (raw as number);
      if (!Number.isFinite(n) || n < minB || n > maxB) {
        const el = this.getCellEl(id, key); this.hardReject(id, key, el || undefined, minB, maxB); return;
      }
      const rounded = Math.round((n + Number.EPSILON) * 100) / 100;
      this.scores[id][key] = String(rounded);
      this.recalcForStudent(id);
      this.debounce(() => this.saveLocalDraft(true), 800)();
    },
    hardReject(id: number, key: string, input?: HTMLInputElement | null, minB?: number, maxB?: number) {
      const lo = (minB ?? this.minScore ?? 0).toFixed(2), hi = (maxB ?? this.maxScore ?? 5).toFixed(2);
      this.errors[this.cellKey(id, key)] = `Valor inválido. Ingresa un número entre ${lo} y ${hi}.`;
      if (input) input.value = "";
      this.scores[id][key] = "";
      this.notify(`El valor ingresado no es válido.\nDebes ingresar un número entre ${lo} y ${hi}.`, "danger");
      this.$nextTick(() => {
        const el = input || this.getCellEl(id, key); if (el) {
          el.focus(); try { el.select?.(); } catch { console.log("hardReject select error"); }
        }
      });
    },
    isOutOfRange(val: number | string | undefined) {
      const minB = this.minScore ?? 0, maxB = this.maxScore ?? 5;
      const v = typeof val === "string" ? Number(val) : val;
      return typeof v === "number" && (v < minB || v > maxB);
    },

    cellRef(id: number, key: string) { return `cell-${id}-${key}`; },
    getCellEl(id: number, key: string): HTMLInputElement | null {
      const r = (this.$refs as any)[this.cellRef(id, key)];
      if (!r) return null;
      return (Array.isArray(r) ? r[0] : r) as HTMLInputElement;
    },
    focusCell(id: number, key: string) {
      this.$nextTick(() => {
        const el = this.getCellEl(id, key); if (el) {
          el.focus(); try { el.select?.(); } catch { console.log("focusCell select error"); }
        }
      });
    },
    blockIfError(id: number, key: string, evt: KeyboardEvent) {
      if (this.cellError(id, key)) { evt.preventDefault(); this.focusCell(id, key); }
      else if (evt.key === "Tab") {
        const dir = (evt as any).shiftKey ? -1 : 1; this.moveFocus(id, key, dir);
      }
    },
    enforceIfInvalid(id: number, key: string) { if (this.cellError(id, key)) { setTimeout(() => this.focusCell(id, key), 0); } },
    moveFocus(id: number, key: string, dir: 1 | -1) {
      const visibleKeys: string[] = [];
      for (const p of this.periodsToDraw) {
        const periodId = Number(p.Id);
        if (!this.visiblePeriods[periodId]) continue;
        const sabers = this.periodSaberMap[periodId] || [];
        for (const s of sabers) {
          if (!this.visibleSabers[s.Code]) continue;
          if (s.competencies.length) { for (const c of s.competencies) visibleKeys.push(this.keyPerf(periodId, s.Code, c.Id)); }
          else { visibleKeys.push(this.keyDirectSaber(periodId, s.Code)); }
        }
      }
      const colIndex = visibleKeys.indexOf(key); if (colIndex < 0) return;
      const studentIds = this.students.map((s) => s.Id);
      const rowIndex = studentIds.indexOf(id); if (rowIndex < 0) return;

      let nextCol = colIndex + dir, nextRow = rowIndex;
      if (nextCol < 0) { nextRow = Math.max(0, rowIndex - 1); nextCol = visibleKeys.length - 1; }
      else if (nextCol >= visibleKeys.length) { nextRow = Math.min(studentIds.length - 1, rowIndex + 1); nextCol = 0; }

      const nextId = studentIds[nextRow], nextKey = visibleKeys[nextCol];
      this.focusCell(nextId, nextKey);
    },

    _debounceTimer: undefined as any,
    debounce(fn: () => void, ms = 500) {
      return () => { if (this._debounceTimer) clearTimeout(this._debounceTimer); this._debounceTimer = setTimeout(fn, ms); };
    },

    /* ========== CONNECTIVITY ========== */
    onOnline() { this.navigatorOnline = true; },
    onOffline() { this.navigatorOnline = false; },

    /* ========== SAVE TO SERVER (per-cell period, not forced to current) ========== */
    codeToSaberId(): Record<string, number> {
      const map: Record<string, number> = {};
      for (const s of this.baseSaberList) map[s.Code] = s.Id;
      return map;
    },

    /** UPDATED: Build payload using the PeriodId parsed from each key (P{periodId}...) */
    buildServerPayload() {
      const codeToId = this.codeToSaberId();
      // allow keys for ALL periods we have in the map
      const validKeys = this.allValidValueKeys();

      const out: any[] = [];
      for (const st of this.students) {
        const row = this.scores[st.Id] || {};
        for (const [k, raw] of Object.entries(row)) {
          if (!validKeys.has(k)) continue;

          const score = typeof raw === "string" ? Number(raw) : (raw as number);
          if (!Number.isFinite(score)) continue;

          const m = /^P(\d+)\.([^.]+)(?:\.(.+))?$/.exec(k);
          if (!m) continue;

          const periodId = Number(m[1]);             // <= parsed period
          const code = m[2];
          const tail = m[3];

          const SaberTypeId = codeToId[code];
          if (!SaberTypeId) continue;

          let PerformanceId: number | null = null;
          if (tail && tail.startsWith("C")) {
            const pid = Number(tail.substring(1));
            PerformanceId = Number.isFinite(pid) ? pid : null;
          }

          const existingId = this.scoreIds?.[st.Id]?.[k] || 0;


          out.push({
            Id: existingId,
            StudentId: st.Id,
            PeriodId: periodId,
            AcademicYearId: this.academicYearId,
            SaberTypeId,
            PerformanceId,                           // filled for competency cells
            Score: Math.max(this.minScore ?? 0, Math.min(this.maxScore ?? 5, score)),
            SubjectId: this.subjectId,
            CourseGroupId: this.courseGroupId,
            GradedByTeacherId : this.teacherId
          });
        }
      }
      return out;
    },

    async persistToServer() {
      if (this.hasValidationErrors) { this.notify("Hay celdas inválidas. Corrígelas antes de guardar.", "warning"); return; }
      if (!this.navigatorOnline) { this.notify("Sin conexión. Guarda como borrador y vuelve a intentar cuando estés en línea.", "warning"); return; }
      const payload = this.buildServerPayload();
      if (!payload.length) { this.notify("No hay cambios para guardar.", "secondary"); return; }

      this.saving = true;
      try {
        this.saveLocalDraft(true);
        await Promise.all(payload.map((p: any) => (AcademicPerformanceGradesService as any).PostPerformance(p)));
        this.notify("Registro completado con éxito.", "success");
      } catch (err: any) {
        console.error("persistToServer error:", err);
        this.notify("No se pudo guardar en el servidor. Se conservó el borrador local.", "danger");
      } finally { this.saving = false; }
    },

    /* ========== PERIOD RESOLUTION (Current + Open Window) ========== */
    async resolveCurrentPeriodForYear(academicYearId: number) {
      let current: any | null = null;
      try {
        current = await (PeriodsService as any).GetCurrentPeriodByAcademicYear({ AcademicYearId: academicYearId });
      } catch {
        current = null;
      }

      const now = new Date();
      const inWindow = (p: any) => {
        const open = p ? p.IsOpen : false;
        const from = p?.OpenFrom ? new Date(p.OpenFrom) : null;
        const to = p?.OpenTo ? new Date(p.OpenTo) : null;
        const afterFrom = from ? now >= from : true;
        const beforeTo = to ? now <= to : true;
        return open && afterFrom && beforeTo;
      };

      const fmtCO = (d?: string | Date | null) => {
        if (!d) return null;
        const dt = typeof d === "string" ? new Date(d) : d;
        const fmt = new Intl.DateTimeFormat("es-CO", {
          timeZone: "America/Bogota",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });
        return fmt.format(dt).replace(", ", " ");
      };

      if (current && current.Id) {
        this.periodId = Number(current.Id);
        this.canEdit = inWindow(current);
        this.periodGate = {
          id: current.Id,
          number: current.PeriodNumber,
          description: current.Description || "",
          openFrom: fmtCO(current.OpenFrom),
          openTo: fmtCO(current.OpenTo),
        };
      } else {
        this.periodId = 0;
        this.canEdit = false;
        this.periodGate = { id: 0, number: 0, description: "", openFrom: null, openTo: null };
      }
    },

    /* ========== LOAD (uses GetPeriodsByAcademicYear) ========== */
    async load() {
      this.loading = true;
      try {
        this.subjects = await SystemSubjectsService.GetSubjects() as ISystemSubjectsModel[];
        this.groups = await CourseGroupsService.GetCourseGroups() as ICourseGroupsModel[];
        const assign = await AcademicAssignmentsService.GetAssignmentById({ Id: this.assignmentId } as any) as any;
        if (!assign) {
          await (AcademicTeacherAssigmentService as any).GetTeacherAssignmentById({ Id: assign?.AssigmentId } as any);
          this.notify("No se encontró el registro de AcademicAssignments.", "danger"); return;
        }

        const assigmentId = Number(assign.AssigmentId);
        this.subjectId = Number(assign.SubjectId);
        this.courseGroupId = Number(assign.CourseGroupId);

        const teacherAssign = await AcademicTeacherAssigmentService.GetTeacherAssignmentById(assigmentId as any);
        if (!teacherAssign) { this.notify("No se encontró el registro de AcademicTeacherAssigment.", "danger"); return; }

        this.academicYearId = Number(teacherAssign.AcademicYearId);
        this.teacherId = Number(teacherAssign.TeacherId);

        // 1.5) Resolve current period & edit gate BEFORE fetching period-bound data
        await this.resolveCurrentPeriodForYear(this.academicYearId);

        // 2) Enrollments for this Group + Year
        const enrollments = await IndividualEnrollmentService.GetEnrollmentsByGroupYear({
          GroupId: this.courseGroupId,
          AcademicYearId: this.academicYearId,
        });
        this.enrollments = enrollments || [];
        const enrolledIds = new Set(this.enrollments.map((e) => e.StudentId));

        // 2.5) PERIODS
        const periods = await PeriodsService.GetPeriodsByAcademicYear({ AcademicYearId: this.academicYearId } as any) as any;

        // 3) Load master lists + ALL grades & ALL competency assignments
        const [
          allStudents,
          sabersRaw,
          weightsRaw,
          competenciesRaw,
          gradesAll,
          assignmentsAll,
          performancesRaw,
        ] = await Promise.all([
          StudentsService.GetStudents(),
          SaberTypesService.GetSaberTypes(),
          AcademicYearSaberWeightsService.GetAcademicYearSaberWeightsByYearId({ AcademicYearId: this.academicYearId } as any),
          AcademicCompetenciesService.GetAcademicCompetencies(),

          // All grades for all periods (fallback to per-period fetch)
          (async () => {
            const svc: any = AcademicPerformanceGradesService as any;
            if (typeof svc.GetByYear === "function") {
              return await svc.GetByYear({
                SubjectId: this.subjectId,
                CourseGroupId: this.courseGroupId,
                AcademicYearId: this.academicYearId,
              } as any);
            }
            const perLists = await Promise.all(
              (periods || []).map((p: any) =>
                AcademicPerformanceGradesService.GetByPeriod({
                  PeriodId: Number(p.Id),
                  SubjectId: this.subjectId,
                  CourseGroupId: this.courseGroupId,
                } as any)
              )
            );
            return ([] as any[]).concat(...perLists);
          })(),

          // All competency assignments (fallback to per-period fetch)
          (async () => {
            const svc: any = CompetencyPeriodAssignmentsService as any;
            if (typeof svc.GetByAcademicYear === "function") {
              return await svc.GetByAcademicYear(this.academicYearId);
            }
            const perLists = await Promise.all(
              (periods || []).map((p: any) => CompetencyPeriodAssignmentsService.GetByPeriodId(Number(p.Id)))
            );
            return ([] as any[]).concat(...perLists);
          })(),

          AcademicPerformancesService.GetAcademicPerformances(),
        ]);

        // 4) Filter students to enrolled only
        const studentsAll = (allStudents || []) as IStudentsModel[];
        this.students = studentsAll
          .filter((s) => enrolledIds.has(s.Id))
          .sort((a, b) => (`${a.Name} ${a.LastName || ""}`).localeCompare(`${b.Name} ${b.LastName || ""}`));

        /* ====== 5) Build per-period saber columns ====== */
        const sabers = (sabersRaw || []) as ISaberTypesModel[];
        const weights = (weightsRaw || []) as ISaberWeightsModel[];
        const competencies = (competenciesRaw || []) as IAcademicCompetenciesModel[];
        const grades = (gradesAll || []) as IAcademicPerformanceGradesModel[];
        const assignments = (assignmentsAll || []) as ICompetencyPeriodAssignmentsModel[];
        const performances = (performancesRaw || []) as IAcademicPerformancesModel[];
        // store periods
        this.periods = periods || [];

        // Default selection: current period (flag currentPeriod / CurrentPeriod) or ALL
        const currentPeriod = (this.periods || []).find(
          (p: any) => (p as any).currentPeriod || (p as any).CurrentPeriod
        );

        if (currentPeriod) {
          this.selectedPeriodId = String((currentPeriod as any).Id);
          this.selectedPeriodIds = [Number((currentPeriod as any).Id)];
        } else {
          this.selectedPeriodId = "";
          this.selectedPeriodIds = (this.periods || []).map((p: any) => Number(p.Id));
        }

        // weight map
        const weightBySaberId = new Map<number, number>();
        weights.forEach((w) => weightBySaberId.set((w as any).SaberTypeId, Number((w as any).WeightPercent) || 0));

        // base sabers for toggles/colors
        const baseSaberList: SaberUi[] = sabers
          .map((s) => ({
            ...s,
            percent: weightBySaberId.get(s.Id) ?? 0,
            color: (s as any).Color || this.colorFromStr(s.Code),
            competencies: [],
          }))
          .filter((s) => s.percent > 0);

        this.baseSaberList = baseSaberList;

        // Initialize visibility
        const saberVisible: Record<string, boolean> = {};
        for (const s of baseSaberList) saberVisible[s.Code] = true;
        this.visibleSabers = saberVisible;

        const periodVisible: Record<number, boolean> = {};
        const currentPid = Number(this.periodId || 0);

        if (
          currentPid &&
          (this.periods || []).some((p: any) => Number(p.Id) === currentPid)
        ) {
          // Solo el período actual visible
          for (const p of this.periods) {
            const pid = Number(p.Id);
            periodVisible[pid] = pid === currentPid;
          }
          // El combo de período apunta al actual
          this.selectedPeriodId = String(currentPid);
        } else {
          // Fallback: si no hay período actual, mostrar todos
          for (const p of this.periods) {
            const pid = Number(p.Id);
            periodVisible[pid] = true;
          }
          this.selectedPeriodId = "";
        }

        this.visiblePeriods = periodVisible;

        // Build competency lookup
        const competencyById = new Map<number, IAcademicCompetenciesModel>();
        for (const c of competencies) competencyById.set(c.Id, c);

        // Build per-period sabers with competencies
        const periodSaberMap: Record<number, SaberUi[]> = {};

        for (const p of this.periods) {
          const periodId = Number(p.Id);

          const sabersForPeriod: SaberUi[] = baseSaberList.map(bs => ({
            ...bs,
            competencies: [],
          }));

          const activeAssignmentIds = new Set<number>(
            (assignments || []).filter(a => a.PeriodId === periodId).map(a => a.CompetencyId)
          );

          // Map for quick lookup: (PeriodId, CompetencyId) -> WeightPercent
          const assignWeightByPeriodComp = new Map<string, number>();
          (assignments || []).forEach(a => {
            const key = `${Number(a.PeriodId)}:${Number(a.CompetencyId)}`;
            assignWeightByPeriodComp.set(key, Number(a.WeightPercent) || 0);
          });

          const perfCols = (performances || [])
            .map((perf) => {
              const comp = competencyById.get(perf.CompetencyId);
              if (!comp) return null;

              // Only show competencies assigned in THIS period (when there are assignments)
              if ((assignments || []).length && !activeAssignmentIds.has(comp.Id)) return null;

              const comboDesc = [perf.Statement, comp.Title, comp.Description].filter(Boolean).join(" — ");

              // 🔴 NEW: pull the weight from assignmentsAll for (periodId, competencyId)
              const percentFromAssignment =
                assignWeightByPeriodComp.get(`${periodId}:${comp.Id}`);

              return {
                perfId: perf.Id,
                competency: comp,
                // prefer assignment weight; fall back to performance weight or 0
                percent: Number(
                  percentFromAssignment ??
                  (typeof perf.WeightPercent !== "undefined" ? perf.WeightPercent : 0)
                ),
                DisplayCode: String(perf.DisplayCode ?? comp.Id),
                Description: comboDesc || comp.Description || comp.Title,
              };
            })
            .filter(Boolean) as Array<{
              perfId: number;
              competency: IAcademicCompetenciesModel;
              percent: number;
              DisplayCode: string;
              Description: string;
            }>;

          for (const s of sabersForPeriod) {
            const enableCompetencies = Boolean((s as any).EnableCompetencies);
            if (!enableCompetencies) {
              s.competencies = [];
              continue;
            }

            const compsForSaber = perfCols
              .filter(pc => pc.competency.SaberTypeId === s.Id)
              .map(pc => ({
                ...(pc.competency as any),
                Id: pc.perfId, // column key is performance Id
                percent: pc.percent,
                DisplayCode: pc.DisplayCode,
                Title: pc.competency.Title,
                Description: pc.Description,
              })) as unknown as Array<IAcademicCompetenciesModel & { percent: number; DisplayCode: string }>;

            compsForSaber.sort((a, b) =>
              a.Title === b.Title
                ? String(a.DisplayCode).localeCompare(String(b.DisplayCode))
                : String(a.Title).localeCompare(String(b.Title))
            );

            s.competencies = compsForSaber;
          }

          periodSaberMap[periodId] = sabersForPeriod;
        }

        this.periodSaberMap = periodSaberMap;

        // Init scores + legacy subtotals
        this.scores = {};
        this.scoreIds = {};
        this.legacySubtotals = {};

        for (const st of this.students) {
          this.scores[st.Id] = {};
          this.scoreIds[st.Id] = {}; // ✅ NEW
          this.legacySubtotals[st.Id] = {};
        }


        // SaberTypeId → Code
        const saberCodeById = new Map<number, string>();
        sabers.forEach((s) => saberCodeById.set(s.Id, s.Code));

        // Seed existing grades across ALL periods
        for (const g of (grades || [])) {
          const periodId = Number(g.PeriodId);
          const saberCode = saberCodeById.get(g.SaberTypeId);
          if (!saberCode) continue;
          if (!enrolledIds.has(g.StudentId)) continue;

          const sabersForPeriod = this.periodSaberMap[periodId] || [];
          const sForCode = sabersForPeriod.find(x => x.Code === saberCode);
          const hasCompetencies = !!sForCode && sForCode.competencies && sForCode.competencies.length > 0;

          const sid = Number(g.StudentId);
          const evalId = Number((g as any).Id || 0);

          if (!g.PerformanceId) {
            if (hasCompetencies) {
              // This is legacy subtotal record (not an editable performance cell)
              const subKey = this.keySubtotal(periodId, saberCode);
              this.legacySubtotals[sid][subKey] = Number(g.Score);

              // If you also want to track its Id, you can store it here too:
              // this.scoreIds[sid][subKey] = evalId;
            } else {
              // Direct saber cell (editable)
              const key = this.keyDirectSaber(periodId, saberCode);
              this.scores[sid][key] = Number(g.Score);
              if (evalId > 0) this.scoreIds[sid][key] = evalId; // ✅
            }
          } else {
            // Performance cell (editable)
            const key = this.keyPerf(periodId, saberCode, Number(g.PerformanceId));
            this.scores[sid][key] = Number(g.Score);
            if (evalId > 0) this.scoreIds[sid][key] = evalId; // ✅
          }
        }

      } finally {
        this.loading = false;
      }
    },

    /* ========== METRICS ========== */
    async loadEvaluationLimits() {
      try {
        const res = await SystemEvaluationMetricsService.GetEvaluationMetrics();
        const all = Array.isArray(res) ? res : [];
        const enabled = all
          .map((x: ISystemEvaluationMetricsModel) => ({
            ...x,
            MinValue: Number(x.MinValue),
            MaxValue: Number(x.MaxValue),
            Enable: Boolean((x as any).Enable ?? x.Enable),
          }))
          .filter((x) => x.Enable && Number.isFinite(x.MinValue) && Number.isFinite(x.MaxValue))
          .sort((a, b) => a.MinValue - b.MinValue);

        this.metrics = enabled;

        const gMin = enabled.length ? Math.min(...enabled.map((x) => x.MinValue)) : 0;
        const gMax = enabled.length ? Math.max(...enabled.map((x) => x.MaxValue)) : 5;

        this.minScore = Number.isFinite(gMin) ? gMin : 0;
        this.maxScore = Number.isFinite(gMax) ? gMax : 5;

        if ((this.maxScore as number) <= (this.minScore as number)) {
          this.minScore = 0; this.maxScore = 5;
        }
      } catch (e) {
        console.warn("GetSystemEvaluationMetrics failed, using defaults 0..5", e);
        this.metrics = []; this.minScore = 0; this.maxScore = 5;
      }
    },

    /* ========== RECALC / UI HELPERS / FLOATING HEADER ========== */
    async recalcForStudent(id: number) { this.scores = { ...this.scores }; await nextTick(); this.syncFloatingHeader(); },

    openPhoto(url?: string | null, initials = "", name = "") {
      this.photo.url = url || null; this.photo.initials = initials; this.photo.name = name; this.photo.open = true;
    },
    formatScore(v?: number) { return v == null ? "—" : Number(v).toFixed(2); },
    notify(text: string, type: "success" | "danger" | "warning" | "info" | "secondary") {
      const typeMap: any = { success: "success", danger: "error", warning: "warning", info: "info", secondary: "info" };
      store.dispatch("core/popupDialog", { text, type: typeMap[type] ?? "info", showCancelButton: false });
    },

    buildFloatingHeader() {
      const tableEl = (this.$refs.scoresTable as any)?.$el as HTMLElement | undefined;
      if (!tableEl) return;
      const liveTop = tableEl.querySelector("thead tr.thead-top-row") as HTMLTableRowElement | null;
      const liveMain = tableEl.querySelector("thead tr.thead-main-row") as HTMLTableRowElement | null;
      const mountTop = this.$refs.floatTopRow as HTMLTableRowElement;
      const mountMain = this.$refs.floatMainRow as HTMLTableRowElement;

      const clear = (el: HTMLElement) => { while (el.firstChild) el.removeChild(el.firstChild); };
      const cloneRowCells = (src: HTMLTableRowElement, dst: HTMLTableRowElement) => {
        const cells = Array.from(src.children) as HTMLTableCellElement[];
        for (const c of cells) {
          const clone = c.cloneNode(true) as HTMLTableCellElement;
          clone.classList.remove("sticky-col-left", "z-3");
          const css = clone.style as CSSStyleDeclaration;
          css.position = ""; css.left = ""; css.right = ""; css.zIndex = "";
          dst.appendChild(clone);
        }
      };

      clear(mountTop); clear(mountMain);
      if (liveTop) cloneRowCells(liveTop, mountTop); else { const th = document.createElement("th"); th.textContent = "Estudiante"; mountTop.appendChild(th); }
      if (liveMain) cloneRowCells(liveMain, mountMain);
    },

    syncFloatingHeader() {
      const tableEl = (this.$refs.scoresTable as any)?.$el as HTMLElement | undefined;
      const host = this.$refs.floatHost as HTMLElement | undefined;
      const scrollHost = this.$refs.tableScroll as HTMLElement | undefined;
      const floatInner = this.$refs.floatInner as HTMLElement | undefined;
      if (!tableEl || !host || !scrollHost || !floatInner) return;

      const rect = scrollHost.getBoundingClientRect();
      const docLeft = rect.left + window.scrollX;
      const width = Math.floor(rect.width);

      this.float.style = { left: `${docLeft}px`, width: `${width}px`, top: `var(--app-fixed-offset, 0px)` };

      floatInner.style.transform = `translateX(-${scrollHost.scrollLeft}px)`;

      const liveMain = tableEl.querySelector("thead tr.thead-main-row") as HTMLTableRowElement | null;
      const liveTop = tableEl.querySelector("thead tr.thead-top-row") as HTMLTableRowElement | null;
      const cloneMain = this.$refs.floatMainRow as HTMLTableRowElement;
      const cloneTop = this.$refs.floatTopRow as HTMLTableRowElement;

      if (liveMain && cloneMain) {
        const liveThs = Array.from(liveMain.children) as HTMLElement[];
        const cloneThs = Array.from(cloneMain.children) as HTMLElement[];
        const len = Math.min(liveThs.length, cloneThs.length);
        for (let i = 0; i < len; i++) {
          const w = liveThs[i].getBoundingClientRect().width;
          cloneThs[i].style.minWidth = `${w}px`;
          cloneThs[i].style.width = `${w}px`;
          cloneThs[i].style.maxWidth = `${w}px`;
          cloneThs[i].style.background = liveThs[i].style.background || "";
        }
      }

      if (liveTop && cloneTop && liveMain) {
        const mainThs = Array.from(liveMain.children) as HTMLElement[];
        const liveTopThs = Array.from(liveTop.children) as HTMLTableCellElement[];
        const cloneTopThs = Array.from(cloneTop.children) as HTMLTableCellElement[];

        const sumWidth = (start: number, span: number) => {
          let sum = 0;
          for (let i = start; i < start + span && i < mainThs.length; i++) sum += mainThs[i].getBoundingClientRect().width;
          return sum;
        };

        let mainIdx = 0;
        for (let i = 0; i < cloneTopThs.length; i++) {
          const span = liveTopThs[i]?.colSpan || 1;
          const totalW = sumWidth(mainIdx, span);
          mainIdx += span;

          const cth = cloneTopThs[i];
          cth.style.minWidth = `${totalW}px`;
          cth.style.width = `${totalW}px`;
          cth.style.maxWidth = `${totalW}px`;
          cth.style.background = liveTopThs[i]?.style.background || "";
        }
      }

      this.onScroll();
    },

    onHScroll() {
      const scrollHost = this.$refs.tableScroll as HTMLElement | undefined;
      const floatInner = this.$refs.floatInner as HTMLElement | undefined;
      if (!scrollHost || !floatInner) return;
      floatInner.style.transform = `translateX(-${scrollHost.scrollLeft}px)`;
    },

    onScroll() {
      const tableEl = (this.$refs.scoresTable as any)?.$el as HTMLElement | undefined;
      const host = this.$refs.floatHost as HTMLElement | undefined;
      const scrollHost = this.$refs.tableScroll as HTMLElement | undefined;
      if (!tableEl || !host || !scrollHost) return;

      const rect = tableEl.getBoundingClientRect();
      const topOffset = this.getFixedTopOffset();

      const within = rect.top < topOffset && rect.bottom > topOffset + 10;
      this.float.visible = within;

      const srect = scrollHost.getBoundingClientRect();
      const docLeft = srect.left + window.scrollX;
      host.style.left = `${docLeft}px`;
      host.style.width = `${Math.floor(srect.width)}px`;
    },

    getFixedTopOffset() {
      const v = getComputedStyle(document.documentElement).getPropertyValue("--app-fixed-offset").trim();
      const n = parseInt(v || "0", 10);
      return isNaN(n) ? 0 : n;
    },
  },
});
